import { LocationProvider, ErrorBoundary, Router, Route, useRoute } from "preact-iso";
import { ServicesProvider } from "./services-context";
import { Services } from "@/services/services";
import { Api } from "@/api";
import { useCallback, useMemo, useState } from "preact/hooks";
import { parseRoutes } from "./router-root";
import { SuspenseProvider } from "./helpers/suspense";
import { ThemeProvider } from "./elements-react/theme-provider";
import { HomePage } from "./pages-react/home-page/HomePage";
import { SearchPage } from "./pages-react/search-page";
import { LayotProvider } from "@/util-react/layout.tsx";
import { PrivacyPolicyPage } from "./pages-react/privacy-policy/PrivacyPolicyPage";
import { AboutUsPage } from "./pages-react/about-us-page/AboutUsPage";
import { WhyCustomersTrustPage } from "@/pages-react/why-customers-trust-us/WhyCustomersTrustPage";
import { WhatWeDoPage } from "@/pages-react/what-we-do/WhatWeDoPage";
import { WhoWeServePage } from "@/pages-react/who-we-serve/WhoWeServePage";
import { PaymentTermsPage } from "./pages-react/payment-terms-page/PaymentTermsPage";
import { LoginPage } from "./pages-react/login-page";
import { HelpPage } from "./pages-react/help/HelpPage";
import { NotFoundPage } from "./pages-react/not-found-page/NotFoundPage";
import { ForgotPasswordPage } from "./pages-react/forgot-password-page";
import { PasswordRecoveryPage } from "@/pages-react/password-recovery-page";
import { SignupPage } from "@/pages-react/signup-page";
import { CartPage } from "@/pages-react/cart-page";
import { FixLoader } from "@/elements-react/fix-loader";
import { DynamicRoute } from "./pages-react/dynamic-page-common/dynamic-route";
import { setConfig } from "@fixparts/testid/index";
import { PreloadedStartupResponse } from "./services/preloaded";
import {
    B2cProductSearchPayload,
    B2cProductsSearchPayload,
    BusinessBrand,
    type CartSimpleQuery
} from "./generated/graphql/b2c";
import { HelmetProvider } from "react-helmet-async";
import { Manifest } from "vite";
import FixHolidayAlert from "@/elements-react/fix-holiday-alert";
import FixHolidayBanner from "@/elements-react/fix-holiday-banner";
import { Entries, manifestToEntries } from "./elements-react/document-elements/head-tags.tsx";
import { supportedLanguages } from "@/constants/languages.ts";
import { TemplateProvider } from "@/util-react/template.tsx";
import { B2bPage } from "@/pages-react/b2b-page/B2bPage.tsx";
import { BrandsPage } from "@/pages-react/brands-page/BrandsPage";
import { WhoWeArePage } from "@/pages-react/who-we-are/WhoWeArePage";
import { ProductRangePage } from "@/pages-react/product-range-page/ProductRangePage.tsx";

setConfig({ environment: import.meta.env.PUBLIC_ENV_FOR_SELECTOR });

const getClientEntries = (): null | Entries => {
  if (!import.meta.env.SSR) {
    try {
      const script = document.getElementById("manifest-data")!;
      const json = JSON.parse(script.textContent);
      if (json) {
        return json as Entries;
      }
    } catch (error) {
      console.error(error);
    }
  }

  return null;
};

export type AppRootProps = {
  host: Element;
  helmet?: {};
  manifest?: Manifest;
  startup?: PreloadedStartupResponse;
  simpleCart?: CartSimpleQuery;
  featuredProducts?: B2cProductSearchPayload[];
  template?: string;
  brandsByCategory?: BusinessBrand[];
  productsSearch?: B2cProductsSearchPayload;
};

export const AppRootReact = (props: AppRootProps) => {
  const [routes, routesMap] = useAppRoutes();
  const services = useMemo(() => {
    // const entries = props.manifest
    //   ? manifestToEntries(props.manifest)
    //   : getClientEntries();

    const services = new Services(props.host, new Api(), null!, routesMap);

    services.api.services = services;
    services.router.setupRoutesMap(routesMap);
    if (import.meta.env.SSR) {
      services.preloaded.setupResponse(props.startup);

      if (props.simpleCart) {
        services.cart.setPreloadedCart(props.simpleCart.Cart);
      }

      if (props.featuredProducts) {
        services.featuredProducts.setPreloadedProducts(props.featuredProducts);
      }

      if (props.brandsByCategory) {
          services.preloaded.brandsByCategory = props.brandsByCategory;
      }

      if (props.productsSearch) {
          services.preloaded.productsSearch = props.productsSearch;
      }
    }

    return services;
  }, [Services, props.host, props.startup, props.simpleCart]);

  return (
    <HelmetProvider context={props.helmet}>
      <SuspenseProvider services={services}>
        <ServicesProvider services={services}>
          <ThemeProvider>
            <LocationProvider>
              <LayotProvider>
                <TemplateProvider defaultTemplate={props.template as string}>
                  <FixHolidayAlert />
                  <FixHolidayBanner />

                  {routes}
                </TemplateProvider>
              </LayotProvider>
            </LocationProvider>
          </ThemeProvider>
        </ServicesProvider>
      </SuspenseProvider>
    </HelmetProvider>
  );
};

const useAppRoutes = () => {
  const [state, setState] = useState({ key: 0, loading: false });
  const [firstLoad, setFirstLoad] = useState(true);

  const onLoadStart = useCallback(() => {
    console.log("onLoadStart");
    setState((prev) => ({ ...prev, loading: true }));
  }, []);

  const onLoadEnd = useCallback(() => {
    console.log("onLoadEnd");
    setState((prev) => ({ ...prev, loading: false }));
    setFirstLoad(false);

    window.dispatchEvent(
      new CustomEvent("route:load-end", {
        detail: { time: Date.now() },
      }),
    );
  }, []);

  const notifyError = useCallback((error: Error) => {
    console.error("Error during route load:");
    console.error(error);
  }, []);

  const update = useCallback(() => {
    setState((prev) => ({ ...prev, key: prev.key + 1 }));
  }, []);

  const HomePageWithLang = () => {
    const route = useRoute();
    const lang = route.params.lang;

    if (lang && !supportedLanguages.some((l) => l.code === lang)) {
      return <NotFoundPage />;
    }

    return <HomePage />;
  };

  // avoid unnecessary re-renders (this prevents the previous route from being displayed while Suspense is pending)
  const routes = useMemo(() => (
    <Router onLoadStart={onLoadStart} onLoadEnd={onLoadEnd}>
      <Route path="/" component={HomePage} data-route="home-page" />
      <Route path="/page-not-found" component={NotFoundPage} data-route="not-found-page" />
      <Route path="/:lang" component={HomePageWithLang} data-route="home-page-with-lang" />
      <Route path="/:lang/login" component={LoginPage} data-route="login-page" />
      <Route path="/:lang/sign-up" component={SignupPage} data-route="signup-page" />
      <Route path="/:lang/forgot-password" component={ForgotPasswordPage} data-route="forgot-password-page" />
      <Route path="/:lang/password-recovery/:token" component={PasswordRecoveryPage} data-route="password-reset-page" />
      <Route path="/:lang/cart" component={CartPage} data-route="cart-page" />
      <Route path="/:lang/privacy-policy" component={PrivacyPolicyPage} data-route="privacy-policy-page" />
      <Route path="/:lang/about-us" component={AboutUsPage} data-route="about-us-page" />
      <Route path="/:lang/why-customers-trust-us" component={WhyCustomersTrustPage} data-route="why-customers-trust-us-page" />
      <Route path="/:lang/payment-terms" component={PaymentTermsPage} data-route="payment-terms-page" />
      <Route path="/:lang/help" component={HelpPage} data-route="help-page" />
      <Route path="/:lang/search" component={SearchPage} data-route="search-page" />
      <Route path="/:lang/b2b-platform" component={B2bPage} data-route="b2b-platform" />
      <Route path="/:lang/brands" component={BrandsPage} data-route="brands-page" />
      <Route path="/:lang/who-we-are" component={WhoWeArePage} data-route="who-we-are-page" />
      <Route path="/:lang/product-range" component={ProductRangePage} data-route="product-range-page" />
      <Route path="/:lang/what-we-do" component={WhatWeDoPage} data-route="what-we-do-page" />
      <Route path="/:lang/who-we-serve" component={WhoWeServePage} data-route="who-we-serve-page" />
      <Route path="/:lang/:dynamicPath+" component={DynamicRoute} data-route="dynamic-route" />
      <Route default component={NotFoundPage} data-route="not-found-page" />
    </Router>
  ), [onLoadStart, onLoadEnd]);

  const routesMap = parseRoutes(routes);
  const element = (
    <ErrorBoundary onError={notifyError}>
      {routes}
      {state.loading && !firstLoad && <FixLoader />}
    </ErrorBoundary>
  );

  return [element, routesMap, update] as const;
};
